import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import translate from '@translate';
import Table from 'src/components/Table';
import { ReturnIcon } from 'src/components/SkladonIcon/Icons';
import {
    GridFilterType,
    GridSortType,
    ProductDetailGridTableRefType,
    RowActionPropsType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType,
} from 'src/types/table';
import { ProductReturnsType } from 'src/types/productDetail';
import { UserSettingsType } from 'src/types/userSettings';
import { ConstantsCodeType, ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { colors } from 'src/themes/default';
import getStatusColor from 'src/utils/getStatusColor';
import colorOptionFce from 'src/utils/colorOptionFce';

const StyledContainer = styled('div')({
    padding: '16px 0',
});

const OPTIONS_SESSION_NAME = 'productReturnsTable';

const allColumns: TableColumnType[] = [
    { label: 'rma', key: 'RMA', type: 'string', defaultShow: true },
    { label: 'eshopReturnId', key: 'ESHOP_RETURN_ID', type: 'string', defaultShow: true },
    { label: 'createdAt', key: 'CREATED_AT', type: 'dateTime', defaultShow: true },
    { label: 'confirmedAt', key: 'CONFIRMED_AT', type: 'dateTime', defaultShow: true },
    {
        label: 'status',
        key: 'STATUS',
        type: 'select',
        selectOption: 'returnStatusCodes',
        customRender: true,
        optionStyleFce: true,
        defaultShow: true,
    },
    {
        label: 'quantity',
        key: 'QUANTITY',
        type: 'number-filter',
        customRender: true,
        defaultShow: true,
    },
    { label: 'received', key: 'RECEIVED', type: 'number-filter', defaultShow: true },
    { label: 'orderId', key: 'ORDER_ID', type: 'number-filter', defaultShow: true },
    { label: 'eshopOrderId', key: 'ESHOP_ORDER_ID', type: 'string', defaultShow: true },
    {
        label: 'recipientName',
        key: 'RECIPIENT_NAME',
        type: 'string',
        defaultShow: false,
        customStyle: { maxWidth: '190px' },
        customStyleHeader: { maxWidth: '190px' },
    },
    {
        label: 'returnDescription',
        key: 'DESCRIPTION',
        type: 'string',
        defaultShow: false,
        customStyle: { maxWidth: '190px' },
        customStyleHeader: { maxWidth: '190px' },
    },
];

const DEFAULT_ITEMS = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

/** Sloupce nutné pro logiku tabulky (rowIdKey, odkaz na vratku, šedá Vráceno ks), nejsou v hlavičce. */
const HIDDEN_GRID_COLUMNS = ['ID', 'RETURNED_ORDER_ID', 'LINE_STATE', 'PRODUCT_ID'] as const;

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'CREATED_AT', direction: 'DESC' },
    page: 1,
    items: DEFAULT_ITEMS,
    columns: [...allColumns],
};

function getInitialTableOptions(): TablePropsOptionsType {
    try {
        const session = JSON.parse(sessionStorage.getItem(OPTIONS_SESSION_NAME) || 'null');
        if (session && typeof session.items === 'number' && ROWS_PER_PAGE_OPTIONS.includes(session.items)) {
            return { ...initTableOptions, ...session, columns: session.columns || initTableOptions.columns };
        }
    } catch {
        // ignore invalid session
    }
    return initTableOptions;
}

export function buildProductReturnsGridPayload(
    options: TablePropsOptionsType,
    idProduct: string
): {
    page: number;
    items: number;
    sorts: GridSortType[];
    filters: GridFilterType[][];
    columns: string[];
} {
    const items = ROWS_PER_PAGE_OPTIONS.includes(options.items) ? options.items : DEFAULT_ITEMS;
    const userConditions = (options.filters || []).flat().filter((c) => c && c.column);
    const productFilter = { column: 'PRODUCT_ID', operator: 'eq' as const, value: idProduct };
    return {
        page: options.page,
        items,
        sorts: [options.sort],
        filters: [[...userConditions, productFilter]],
        columns: [...allColumns.map((c) => c.key), ...HIDDEN_GRID_COLUMNS],
    };
}

export interface ProductReturnsTablePropsType {
    data: ProductReturnsType | null;
    loading?: boolean;
    onFetchData: (payload: ReturnType<typeof buildProductReturnsGridPayload>) => void;
    idProduct: string;
    returnStatusCodes: ConstantsCodeType[];
    returnStatusCodesObject: ConstantsCodesObjectType;
    permissionReturnDetail: boolean;
    userSettingsData: UserSettingsType | null;
    editUserSettings: (newUserSettings: UserSettingsType) => void;
}

const ProductReturnsTable = forwardRef<ProductDetailGridTableRefType, ProductReturnsTablePropsType>((props, ref) => {
    const tableRef = useRef<TableRefType>(null);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(getInitialTableOptions);

    const rows: TableRowType[] = props.data?.data ?? [];
    const totalCount = props.data?.totalCount ?? 0;

    const loadTableData = useCallback(() => {
        if (props.idProduct && tableOptions.columns.length) {
            props.onFetchData(buildProductReturnsGridPayload(tableOptions, props.idProduct));
        }
    }, [props.idProduct, props.onFetchData, tableOptions]);

    useImperativeHandle(
        ref,
        (): ProductDetailGridTableRefType => ({
            exportTable: (type, filename, getPrintRows, extendedColmns) =>
                tableRef.current?.exportTable(type, filename, getPrintRows, extendedColmns),
            exportTableGrid: (type, filename, getGridParams, requestUrl, extendedColmns, extendOptions) =>
                tableRef.current?.exportTableGrid(
                    type,
                    filename,
                    getGridParams,
                    requestUrl,
                    extendedColmns,
                    extendOptions
                ),
            getGridParams: () => buildProductReturnsGridPayload(tableOptions, props.idProduct),
        }),
        [tableOptions, props.idProduct]
    );

    useEffect(() => {
        loadTableData();
    }, [tableOptions]);

    const handleChangeOptions = useCallback((newOptions: TablePropsOptionsType) => {
        setTableOptions(newOptions);
    }, []);

    const customRender = useCallback(
        (column: TableColumnType, row: TableRowType) => {
            const value = row[column.key];
            switch (column.key) {
                case 'CREATED_AT':
                case 'CONFIRMED_AT':
                    return value ? moment(value as string).format('DD.MM.YYYY HH:mm') : '';
                case 'STATUS':
                    return (
                        <span style={{ color: getStatusColor(props.returnStatusCodesObject[row.STATUS]?.color) }}>
                            {props.returnStatusCodesObject[row.STATUS]?.label}
                        </span>
                    );
                case 'QUANTITY':
                    if (String(row.LINE_STATE ?? '').toUpperCase() === 'D') {
                        return <span style={{ color: colors.text_T3 }}>{value}</span>;
                    }
                    return value;
                default:
                    return value;
            }
        },
        [props.returnStatusCodesObject]
    );

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <ReturnIcon />,
            label: translate('returns.action.returnDetail'),
            getLink: (row) => `/returns/${row.RETURNED_ORDER_ID}/`,
            hiddenFce: () => !props.permissionReturnDetail,
        },
    ];

    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        returnStatusCodes: props.returnStatusCodes,
    };

    return (
        <StyledContainer>
            <Table
                ref={tableRef}
                rowIdKey="ID"
                rows={rows}
                allColumns={allColumns}
                columsLabelPrefix="productDetail.productReturnsTable"
                options={tableOptions}
                totalCount={totalCount}
                loading={props.loading}
                customRender={customRender}
                heightVariant="top-tab"
                fullHeight
                optionsSessionName={OPTIONS_SESSION_NAME}
                selectOptions={tableSelectOptions}
                onChangeOptions={handleChangeOptions}
                showClearFilters
                rowActions={tableRowActions}
                optionStyleFce={colorOptionFce}
                userSettingsName="showProductDetailReturnsTableColumns"
                userSettingsData={props.userSettingsData}
                editUserSettings={props.editUserSettings}
            />
        </StyledContainer>
    );
});

ProductReturnsTable.displayName = 'ProductReturnsTable';

export default ProductReturnsTable;
